import React from 'react';
import { CheckCircle2, Circle, Pencil, Calendar, Tag, RotateCcw, Archive, ArchiveRestore, Repeat, Users } from 'lucide-react';
import './ActivityCard.css';

const CATEGORY_COLORS = {
  home: 'blue', errands: 'purple', finance: 'green',
  health: 'red', work: 'yellow', personal: 'default'
};

const PRIORITY_MAP = {
  high: { label: 'High', color: 'red' },
  medium: { label: 'Med', color: 'yellow' },
  low: { label: 'Low', color: 'blue' },
};

export default function ActivityCard({ activity, onToggle, onEdit, onToggleActive, style, currentUser }) {
  const isCompleted = activity.status === 'completed';
  const isInactive = activity.active === false;
  const prio = PRIORITY_MAP[activity.priority] || PRIORITY_MAP.medium;
  const catColor = CATEGORY_COLORS[activity.category] || 'default';
  const isRecurring = activity.isRecurring || false;

  // Permission: admin OR owner can act on this chore
  const canManage = currentUser && (
    currentUser.role === 'admin' ||
    String(activity.userId) === String(currentUser.id)
  );

  const isOverdue = activity.dueDate && !isCompleted && !isInactive &&
    new Date(activity.dueDate) < new Date(new Date().toDateString());

  const handleToggleActive = () => {
    onToggleActive(activity.id);
  };

  return (
    <div
      className={`activity-card fade-in ${isCompleted ? 'card-completed' : ''} ${isOverdue ? 'card-overdue' : ''} ${isInactive ? 'card-inactive' : ''}`}
      style={style}
    >
      <button
        className="check-btn"
        onClick={() => canManage && onToggle(activity.id)}
        disabled={!canManage}
        title={canManage ? (isCompleted ? 'Mark as pending' : 'Mark as complete') : 'View only'}
      >
        {isCompleted
          ? <CheckCircle2 size={20} className="check-done" />
          : <Circle size={20} className="check-empty" />
        }
      </button>

      <div className="card-body">
        <div className="card-top">
          <span className={`card-title ${isCompleted ? 'title-done' : ''}`}>{activity.title}</span>
          <div className="card-badges">
            {isInactive && <span className="badge badge-inactive">Inactive</span>}
            {isRecurring && (
              <span className="badge badge-recurring" title={`Recurring ${activity.recurrenceType || 'weekly'}`}>
                <Repeat size={10} />
                Recurring
              </span>
            )}
            <span className={`badge badge-${prio.color}`}>{prio.label}</span>
            <span className={`badge badge-cat badge-${catColor}`}>
              <Tag size={10} />
              {activity.category}
            </span>
          </div>
        </div>

        {activity.notes && (
          <p className="card-notes">{activity.notes}</p>
        )}

        <div className="card-meta">
          {activity.dueDate && (
            <span className={`meta-date ${isOverdue ? 'overdue' : ''}`}>
              <Calendar size={11} />
              {isOverdue ? 'Overdue · ' : ''}
              {new Date(activity.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {activity.assignedUsers && activity.assignedUsers.length > 0 && (
            <span className="meta-assigned" title={`Assigned to ${activity.assignedUsers.length} user(s)`}>
              <Users size={11} />
              {activity.assignedUsers.length} assigned
            </span>
          )}
          <span className="meta-created">
            Added {new Date(activity.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
      </div>

      <div className="card-actions">
        {canManage && (
          <button
            className={`action-btn action-complete ${isCompleted ? 'is-active' : ''}`}
            onClick={() => onToggle(activity.id)}
            disabled={isInactive}
            title={isInactive ? 'Reactivate first to change status' : (isCompleted ? 'Reopen (mark as pending)' : 'Complete')}
          >
            {isCompleted ? <RotateCcw size={14} /> : <CheckCircle2 size={14} />}
          </button>
        )}
        <button
          className="action-btn"
          onClick={() => onEdit(activity)}
          disabled={isInactive}
          title={isInactive ? 'Reactivate first to edit' : 'Edit'}
        >
          <Pencil size={14} />
        </button>
        {canManage && (
          <button
            className={`action-btn action-deactivate ${isInactive ? 'is-inactive' : ''}`}
            onClick={handleToggleActive}
            title={isInactive ? 'Reactivate' : 'Deactivate'}
          >
            {isInactive ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
